/**
 * Conversions between the stored answer key (`{ layers: { digits, … } }`) and the board state
 * the author fills in. Layers this type does not draw — regions, modifiers, coloring typed by
 * hand in the JSON tab — are carried through untouched.
 */
import type {
  CellId,
  DigitLayers,
  GridSudokuDefinition,
  GridSudokuProgress,
  GridSudokuSubmission,
} from "@/lib/puzzle-format/types";
import { buildGeometry } from "./geometry";
import {
  emptyProgress,
  givensOf,
  hasModifiers,
  hasRegionDrawing,
  hasShading,
  labelMap,
  hasSuperposition,
  modifierMap,
  MODIFIERS_LAYER,
  numberMap,
  SUPERPOSITION_LAYER,
  REGIONS_LAYER,
  shadeMap,
  SHADING_LAYER,
} from "./progress";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function emptySolution(_definition: GridSudokuDefinition): GridSudokuSubmission {
  return { layers: { digits: {} } };
}

/** Reads whatever the author typed, however wrong, and returns the layers it can use. */
export function layersOf(value: unknown): DigitLayers {
  const layers = isRecord(value) && isRecord(value.layers) ? value.layers : {};
  const digits: Record<CellId, number> = {};
  if (isRecord(layers.digits)) {
    for (const [cell, digit] of Object.entries(layers.digits)) {
      if (typeof digit === "number" && Number.isFinite(digit)) digits[cell] = digit;
      else if (typeof digit === "string" && /^\d+$/.test(digit)) digits[cell] = Number(digit);
    }
  }
  return { ...(layers as Partial<DigitLayers>), digits };
}

/** Board state for the editor: the key minus the givens, which the grid draws on its own. */
export function progressFromSolution(
  definition: GridSudokuDefinition,
  value: unknown,
): GridSudokuProgress {
  const givens = givensOf(definition);
  const layers = layersOf(value);
  const digits: Record<CellId, number> = {};
  for (const [cell, digit] of Object.entries(layers.digits)) {
    if (!(cell in givens)) digits[cell] = digit;
  }
  return {
    ...emptyProgress(definition),
    digits,
    shading: shadeMap(layers[SHADING_LAYER]),
    regions: labelMap(layers[REGIONS_LAYER]),
    modifiers: modifierMap(layers[MODIFIERS_LAYER]),
    superposition: numberMap(layers[SUPERPOSITION_LAYER]),
  };
}

/** The key as stored: filled cells plus the givens, on top of any layer typed by hand. */
export function solutionFromProgress(
  definition: GridSudokuDefinition,
  progress: GridSudokuProgress,
  previous: unknown,
): GridSudokuSubmission {
  const layers = layersOf(previous);
  const shading = hasShading(definition)
    ? { [SHADING_LAYER]: { ...(progress.shading ?? {}) } }
    : {};
  const regions = hasRegionDrawing(definition)
    ? { [REGIONS_LAYER]: { ...(progress.regions ?? {}) } }
    : {};
  const modifiers = hasModifiers(definition)
    ? { [MODIFIERS_LAYER]: { ...(progress.modifiers ?? {}) } }
    : {};
  const superposition = hasSuperposition(definition)
    ? {
        [SUPERPOSITION_LAYER]: Object.fromEntries(
          Object.entries(progress.superposition ?? {}).map(([cell, d]) => [cell, String(d)]),
        ),
      }
    : {};
  return {
    ...(isRecord(previous) ? previous : {}),
    layers: {
      ...layers,
      digits: { ...progress.digits, ...givensOf(definition) },
      ...shading,
      ...regions,
      ...modifiers,
      ...superposition,
    },
  };
}

/**
 * How many cells are complete on every layer the key needs: a digit, plus a shading decision
 * and a region when the puzzle has them. The one number that says a key is finished.
 */
export function solutionCoverage(
  definition: GridSudokuDefinition,
  progress: GridSudokuProgress,
): { filled: number; total: number } {
  const geom = buildGeometry(definition);
  const givens = givensOf(definition);
  const shading = hasShading(definition) ? (progress.shading ?? {}) : null;
  const regions = hasRegionDrawing(definition) ? (progress.regions ?? {}) : null;
  const filled = geom.cells.filter(
    (cell) =>
      (cell.id in givens || cell.id in progress.digits) &&
      (shading === null || cell.id in shading) &&
      (regions === null || cell.id in regions),
  );
  return { filled: filled.length, total: geom.cells.length };
}
